<template>
  <v-col class="summary-payment-information" lg="6" md="7" sm="12">
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      />
    </v-overlay>
    <v-alert
      width="100%"
      v-if="!!errorMessage"
      dismissible
      type="error"
    >{{ errorMessage }}</v-alert>
    <v-dialog v-model="showSuccessRegister" width="500">
      <v-card class="member-register-modal">
        <v-card-title>
          <div />
          <h2>{{isRenew ? 'Renewal' : 'Registration'}} Confirmed</h2>
          <div />
        </v-card-title>
        <p>Congratulations! Your {{isRenew ? 'renewal' : 'registration'}} is complete.</p>
        <v-btn
          dark
          large
          color="primary"
          v-on:click="returnHome()"
        >RETURN HOME</v-btn>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSuccessEmailRegister" width="500">
      <v-card class="member-register-modal">
        <v-card-title>
          <div />
          <h2>Email(s) Sent</h2>
          <div />
        </v-card-title>
        <p>An email was sent to the following email addresses with an invitation to setup an account.</p>
        <div v-if="sentEmails.length" class="d-flex flex-column align-center mb-2">
          <div v-for="sentEmail of sentEmails.concat(failedEmails)" :key="sentEmail">{{sentEmail}}</div>
        </div>
        <div v-if="failedEmails.length">
          <p>We failed to send the invitation to the following email addresses</p>
          <div class="d-flex flex-column align-center">
            <div v-for="failedEmail of failedEmails" :key="failedEmail">{{failedEmail}}</div>
          </div>
        </div>
        <v-btn
          dark
          large
          color="primary"
          v-on:click="returnHome()"
        >RETURN HOME</v-btn>
      </v-card>
    </v-dialog>
    <div class="top-icons">
      <a
        class="back-icon"
        v-on:click="goBack"
      >
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
      </a>
    </div>
    <h1 class="register-title">Summary & Payment Information</h1>

    <div>
      <div>PRIMARY MEMBER{{ isDual ? ' AND SECONDARY MEMBER' :  ''}}</div>
      <div class="plans-container">
        <div v-if="!!plan" class="plan-container" :key="plan.id">
          <div>{{plan.title}}</div>
          <div>${{plan.price}}</div>
        </div>
        <div v-if="subscribe" class="plan-container">
          <div>{{firstClass ? firstClassText() : standardText()}}</div>
          <div>{{`$${postagePrice}`}}</div>
        </div>
      </div>
    </div>

    <div v-if="!isJunior && juniorCount !== 0">
      <div>JUNIOR MEMBER(s)</div>
      <div class="plans-container">
        <div class="plan-container">
          <div>{{juniorCount}} yearly membership(s)</div>
          <div>$0</div>
        </div>
      </div>
    </div>

    <div class="total-container">
      <div class="total">
          <p>TOTAL:</p>
          <p>${{total}}</p>
      </div>
      <p>All prices shown are in USD.</p>
    </div>
    <div>
      <p>I (We) agree to abide by the <ASCAArtIncLink />, <BylawsLink />, and all ASCA Rules and Regulations governing the ASCA Registry and all other ASCA programs. </p>
      <p>By submitting this application and including an email address, I (We) agree to receive email communications from ASCA including, but not limited to, surveys, election information and balloting, marketing and other important notifications with the option to opt out of any or all mass communications.</p>
      <p>By submitting this form, I(we) also agree to have email opening/review tracked where done for ASCA election integrity. If you are not consenting to tracking of email balloting, please alert the Business Office.</p>
      <div class="agreement-container">
          <div class="agreement-input-container">
              <div class="agreement-input">
                  <p>X</p>
                  <v-text-field
                      flat
                      solo
                      label="Member's Digital Signature Here"
                      type="text"
                      v-model="signature"
                      hide-details="auto"
                  />
              </div>
              <p class="agreement-date"><localized-date :date="agreementDate" format="long" /></p>
          </div>
      </div>
    </div>
    <hosted-registration-payment
      v-if="total != 0"
      class="align-self-center"
      :disabled="!signature"
      buttonText="SUBMIT"
      v-on:updatePaymentInfo="pay"
    />
    <v-btn
      v-else
      :dark="!!signature"
      :disabled="!signature"
      class="white--text align-self-center"
      large
      color="primary"
      v-on:click="() => pay(null)"
    >SUBMIT</v-btn>
    <div class="footer-container">
      <v-img height="2rem" contain :src="logoImage" />
      <footer-text />
    </div>
  </v-col>
</template>

<script lang="ts">
import Vue from 'vue';
import FooterText from '@/components/common/FooterText.vue';
import ASCAArtIncLink from "@/components/common/ASCAArtIncLink.vue";
import BylawsLink from "@/components/common/BylawsLink.vue";
import HostedRegistrationPayment from '@/components/common/HostedRegistrationPayment.vue';

import userService from '@/services/portal/userService';
import memberService from '@/services/portal/memberService';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import { getUserToday } from '@/helpers/date-utils';

import AuthorizeNetResponseData from '@/types/AuthorizeNetResponseData';
import { UserInfo } from '@/types/User';
import { MemberType } from '@/types/Member';
import { Countries } from "@/constants";

const logoImage = require('@/assets/logo.svg');

export default Vue.extend({
  name: 'SummaryPaymentInformation',
  components: { FooterText, ASCAArtIncLink, BylawsLink, HostedRegistrationPayment },
  data() {
    return {
      logoImage,
      isValidCard: false,
      isProcessing: false,
      errorMessage: '',

      postagePrice: 0,
      isDual: false,
      isJunior: false,
      subscribe: false,
      firstClass: false,
      juniorCount: 0,

      total: 0,
      signature: '',
      agreementDate: getUserToday(),

      showSuccessRegister: false,
      showSuccessEmailRegister: false,
      sentEmails: [],
      failedEmails: [],
    }
  },
  watch: {
    membership: function(newValue) {
      if (newValue && newValue.plan) {
        const plan = newValue.plan;
        const subscribe = newValue.subscribe;
        const firstClass = newValue.firstClass;

        const isJunior = plan.memberType === MemberType.Junior;
        const isDual = plan.MemberType === MemberType.Lifetime || plan.memberType === MemberType.LifetimeDual;

        const postagePrice = subscribe
          ? plan.aussieTimesPrice + (firstClass ? this.firstClassPrice : this.standardPrice)
          : 0;

        const total = plan.price + postagePrice;

        this.isJunior = isJunior;
        this.isDual = isDual;
        this.total = total;
        this.postagePrice = postagePrice;
        this.subscribe = subscribe;
        this.firstClass = firstClass;
        this.juniorCount = (newValue.juniors || []).length;
      }
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    membership() {
      return this.$store.state.registerRenewMembershipInfo;
    },
    isRenew() {
      return this.$store.state.registerRenewMembershipInfo.isRenew;
    },
    plan() {
      return this.$store.state.registerRenewMembershipInfo.plan;
    },
    standardPrice() {
      const primary = this.$store.state.registerRenewMembershipInfo.primary;
      const aussieTimeFees = this.$store.state.registerRenewMembershipInfo.aussieTimeFees;

      if (!!aussieTimeFees && primary.country === Countries.Canada) {
        return aussieTimeFees.surfacePostageCanada;
      } else if (!!aussieTimeFees && primary.country !== Countries.USA) {
        return aussieTimeFees.surfacePostageOther;
      }

      return 0;
    },
    firstClassPrice() {
      const primary = this.$store.state.registerRenewMembershipInfo.primary;
      const aussieTimeFees = this.$store.state.registerRenewMembershipInfo.aussieTimeFees;

      let price = 25;

      if (!!aussieTimeFees && primary.country === Countries.USA){        
        return aussieTimeFees.firstClassUSA;
      } else if(!!aussieTimeFees && primary.country === Countries.Canada) {
        return aussieTimeFees.firstClassUSA;
      } else if (!!aussieTimeFees) {
        return aussieTimeFees.firstClassOther;
      }

      return price;
    }
  },
  methods: {
    standardText() {
      const aussiePrice = `1-Year Subscription to Aussie Times ${!!this.plan.aussieTimesPrice ? `($${this.plan.aussieTimesPrice}/yr)` : ''}`;
      const standardPostage = `Standard Postage ${!!this.standardPrice ? `($${this.standardPrice})` : ''}`;

      return `${aussiePrice} + ${standardPostage}`;
    },
    firstClassText() {
      const aussiePrice = `1-Year Subscription to Aussie Times ${!!this.plan.aussieTimesPrice ? `($${this.plan.aussieTimesPrice}/yr)` : ''}`;
      const firstClassPostage = `First Class Postage ($${this.firstClassPrice})`;

      return `${aussiePrice} + ${firstClassPostage}`;
    },
    goBack() {
      const plan = this.membership.plan;

      this.$store.commit('changeRegisterRenewMembershipInfo', {
        page: plan.memberType === MemberType.Service ? 1 : 2,
      });
    },
    returnHome() {
      this.$navigate('/');
      this.$store.commit('resetRegisterRenewMembershipInfo');
    },
    pay: async function (data: AuthorizeNetResponseData | null) {
      this.isProcessing = true;

      try {
        const region = normalizeAddressRegion(
          this.membership.primary.country,
          this.membership.primary.stateProvince,
          this.membership.primary.international
        );
        const primary = {
          ...this.membership.primary,
          stateProvince: region.state,
          international: region.international,
        };
        const response = this.membership.isRenew
          ? await memberService.renewMembership({
              signature: this.signature,
              primary,
              secondary: this.membership.secondary,
              juniors: this.membership.juniors,
              membershipType: this.membership.plan.memberType,
              subscribeToAussieTimes: this.membership.subscribe,
              firstClassSubscription: this.membership.firstClass,
              fee: this.total,
              paymentInfo: data
            })
          : await memberService.registerMembership({
              signature: this.signature,
              primary,
              secondary: this.membership.secondary,
              juniors: this.membership.juniors,
              membershipType: this.membership.plan.memberType,
              subscribeToAussieTimes: this.membership.subscribe,
              firstClassSubscription: this.membership.firstClass,
              fee: this.total,
              paymentInfo: data
            });
        
        if (response.success) {
          const newUserInfo = await userService.getProfile();

          this.$store.commit('loadUserInfo', newUserInfo);
          this.isProcessing = false;

          if (!response.emailsSent.length && !response.emailsFailed.length) {
            this.showSuccessRegister = true;
          } else {
            this.sentEmails = response.emailsSent;
            this.failedEmails = response.emailsFailed;
            this.showSuccessEmailRegister = true;
          }
        } else {
          this.isProcessing = false;
          this.errorMessage = response.errors ? response.errors.join(',') : 'An Unknown Error Occured';
        }
      } catch {
        this.isProcessing = false;
        this.errorMessage = 'An Unknown Error Occured';
      }

      this.isProcessing = false;
    },
  },
});
</script>
